'use client';

/**
 * The payer's balance poll (Guest QR Platform, piece 3, Task 8).
 *
 * `useGuestCheck` keeps `CheckPayPage`'s view of ONE check current while the
 * guest is actually looking at it. Sibling of `useTableCheck` and deliberately
 * the same shape — React Query `refetchInterval` with an EXPLICIT visibility
 * gate — for the reasons that hook's own header gives: the interval must be
 * provably `false` while hidden (a test can read the option; it cannot read the
 * focus manager's mind), and a tab coming back must refetch IMMEDIATELY rather
 * than wait out a tick.
 *
 * There is no realtime channel here, for the same reason: the check-pay routes
 * are unauthenticated (the check token IS the credential), so a signed-out
 * browser could not open a Firestore listener on a check even if we wanted one.
 *
 * ── Why 3 seconds rather than the table page's 15 ───────────────────────────
 * This is the surface where two people pay the same check at the same time.
 * The number the guest is about to press Pay on is the balance, and the window
 * in which it goes stale is the window in which someone else at the table is
 * settling part of it. The route is sized for exactly this (120/min for the
 * token:ip pair — the poll tier, not the 6/min spend tier), and the visibility
 * gate is what keeps six idle phones from spending that budget on a bill nobody
 * is looking at.
 *
 * The server is still the authority: it re-reads the balance AFTER taking the
 * check's pay lock and caps or refuses there. This poll is what stops the page
 * from ASKING for a figure that has already been paid.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import type { GuestCheckView } from '@order-up/common';

import { getGuestCheck, isCheckMiss } from '../services/publicChecks';
import { useDocumentVisible } from './useGuestTable';

/** React Query key factory for everything keyed by a CHECK access token. */
export const guestCheckKeys = {
  all: ['guestCheck'] as const,
  check: (checkToken: string) => [...guestCheckKeys.all, 'check', checkToken] as const,
};

/** How often the check is re-read while the payer is looking at the page. */
export const CHECK_POLL_MS = 3_000;

/**
 * The query options for one check, as a pure function of the token and whether
 * the page is visible.
 *
 * Separated out so the poll's central claim — *no interval while hidden* — is
 * something a test can assert directly, instead of inferring it from timers.
 */
export function guestCheckQueryOptions({
  checkToken,
  visible,
}: {
  checkToken: string;
  visible: boolean;
}): UseQueryOptions<GuestCheckView, Error> {
  return {
    queryKey: guestCheckKeys.check(checkToken),
    queryFn: () => getGuestCheck(checkToken),
    enabled: !!checkToken,
    refetchInterval: visible ? CHECK_POLL_MS : false,
    refetchIntervalInBackground: false,
    // OFF, so the visibility gate below is the ONLY thing that triggers a
    // refetch. React Query's focusManager fires on the same `visibilitychange`
    // event `useDocumentVisible` listens to, so leaving it on sends two
    // requests for one moment of the guest picking their phone back up — and
    // makes the gate untestable, because a passing test could be either
    // mechanism.
    refetchOnWindowFocus: false,
    // A bill is never fresh. Anything above zero shows a payer a balance that
    // someone else at the table has already moved.
    staleTime: 0,
    // A miss means the token stopped being live (expired, or the check was
    // voided/superseded). Retrying that is a request guaranteed to fail the
    // same way.
    retry: (failureCount, error) => !isCheckMiss(error) && failureCount < 2,
  };
}

/**
 * One check's guest-facing projection, kept current while the payer watches.
 *
 * The error is surfaced (unlike `useTableCheck`, which swallows it): this page
 * exists to take money against the figure it shows, so a read it could not
 * refresh has to be visible rather than quietly stale.
 */
export function useGuestCheck(checkToken: string) {
  const visible = useDocumentVisible();
  const query = useQuery(guestCheckQueryOptions({ checkToken, visible }));

  // Fire on the hidden → visible EDGE only. Keyed on the transition rather than
  // on `visible` itself, or the mount would fetch twice.
  const { refetch } = query;
  const wasVisible = useRef(visible);
  useEffect(() => {
    if (visible && !wasVisible.current) void refetch();
    wasVisible.current = visible;
  }, [visible, refetch]);

  return query;
}
